import { useMemo, useState } from "react";
import type { Polygon } from "geojson";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { parsePastedGeoJson } from "../geojson-import";

const PLACEHOLDER = `{
  "type": "Polygon",
  "coordinates": [[[2.35, 48.85], [2.36, 48.85], [2.36, 48.86], [2.35, 48.85]]]
}`;

type PaymentZoneImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (polygons: Polygon[]) => boolean;
};

export const PaymentZoneImportDialog = ({ open, onOpenChange, onImport }: PaymentZoneImportDialogProps) => {
  const [raw, setRaw] = useState("");

  const result = useMemo(() => (raw.trim() ? parsePastedGeoJson(raw) : null), [raw]);

  const close = () => {
    setRaw("");
    onOpenChange(false);
  };

  const handleImport = () => {
    if (!result?.ok) return;
    if (onImport(result.polygons)) {
      close();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setRaw("");
        onOpenChange(next);
      }}
    >
      <DialogContent showCloseButton>
        <DialogHeader>
          <DialogTitle>Importer un GeoJSON</DialogTitle>
          <DialogDescription>
            Collez un Polygon ou un MultiPolygon (un Feature ou une FeatureCollection sont aussi acceptés) pour créer la
            géométrie sans la dessiner à la main.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={raw}
          onChange={(event) => setRaw(event.target.value)}
          placeholder={PLACEHOLDER}
          spellCheck={false}
          className="min-h-44 font-mono text-xs"
        />

        <div className="min-h-5 text-xs">
          {result?.ok && (
            <p className="text-emerald-600">
              {result.polygonCount} polygone{result.polygonCount > 1 ? "s" : ""} · {result.vertexCount} sommet
              {result.vertexCount > 1 ? "s" : ""} prêt{result.polygonCount > 1 ? "s" : ""} à l'import.
            </p>
          )}
          {result && !result.ok && <p className="text-destructive">{result.error}</p>}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={close}>
            Annuler
          </Button>
          <Button onClick={handleImport} disabled={!result?.ok}>
            Importer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
